import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, Link } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from 'sonner';
import { ArrowRight } from 'lucide-react';
import { analytics } from './hooks/useAnalytics';
import { AuthProvider, useAuth } from './context/AuthContext';
import LandingPage from './pages/LandingPage';
import CaseMethodPage from './pages/CaseMethodPage';
import ConsultingPage from './pages/ConsultingPage';
import ResultsPage from './pages/ResultsPage';
import MentorsPage from './pages/MentorsPage';
import DeadlinesGridPage from './pages/DeadlinesGridPage';
import DeadlinesSchoolPage from './pages/DeadlinesSchoolPage';
import DeadlinesStatePage from './pages/DeadlinesStatePage';
import DeadlinesCollectionPage from './pages/DeadlinesCollectionPage';
import BlogPage from './pages/BlogPage';
import BlogPostPage from './pages/BlogPostPage';
import DiagnosticPage from './pages/DiagnosticPage';
import PortalPage from './pages/PortalPage';
import PremiumPage from './pages/PremiumPage';
import ComparePage from './pages/ComparePage';
import SignInPage from './pages/SignInPage';
import SignUpPage from './pages/SignUpPage';
import PaymentSuccessPage from './pages/PaymentSuccessPage';
import AppLayout from './components/AppLayout';
import Dashboard from './pages/Dashboard';
import CollegeList from './pages/CollegeList';
import EssayStudio from './pages/EssayStudio';
import ActivitiesPage from './pages/ActivitiesPage';
import DocumentVault from './pages/DocumentVault';
import RecommendersPage from './pages/RecommendersPage';
import TimelinePage from './pages/TimelinePage';
import ProfilePage from './pages/ProfilePage';
import AdminPage from './pages/AdminPage';
import AdmissionsFactsPage from './pages/AdmissionsFactsPage';
import SchoolProfilesPage from './pages/SchoolProfilesPage';
import EssayGuidePage from './pages/EssayGuidePage';
import TimelineGuidePage from './pages/TimelineGuidePage';
import CalculatorPage from './pages/CalculatorPage';
import ResourcesPage from './pages/ResourcesPage';
import ZipCodePage from './pages/ZipCodePage';
import EliteStrategyPage from './pages/EliteStrategyPage';
import SchoolExplorerPage from './pages/SchoolExplorerPage';
import PaywallPage from './pages/PaywallPage';
import ResumePage from './pages/ResumePage';
import InterviewPrepPage from './pages/InterviewPrepPage';
import ChecklistPage from './pages/ChecklistPage';
import CareerInsightsPage from './pages/CareerInsightsPage';
import CareerComparePage from './pages/CareerComparePage';
import CollegeReadinessPage from './pages/CollegeReadinessPage';
import CareerPortalPage from './pages/CareerPortalPage';
import WhyHirePage from './pages/WhyHirePage';
import CantAffordPage from './pages/CantAffordPage';
import ConsultationPage from './pages/ConsultationPage';
import LawPortalLayout from './components/LawPortalLayout';
import LawDashboard from './pages/law/LawDashboard';
import LawProfile from './pages/law/LawProfile';
import LawSchools from './pages/law/LawSchools';
import LawApplications from './pages/law/LawApplications';
import LawEssays from './pages/law/LawEssays';
import ChatWidget from './components/ChatWidget';
import { usePageTracker } from './hooks/usePageTracker';
import './App.css';

function ProtectedRoute({ children, bypassPaywall = false }) {
  const { isAuthenticated, user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F9F7]">
        <div className="text-center space-y-3">
          <div className="w-8 h-8 border-2 border-[#0F392B] border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="text-sm text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }
  if (!isAuthenticated) return <Navigate to="/sign-in" replace />;
  if (!bypassPaywall && !user?.has_paid) return <Navigate to="/paywall" replace />;
  return children;
}

function PublicRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return null;
  return isAuthenticated ? <Navigate to="/dashboard" replace /> : children;
}

const MARKETING_PATHS = ['/', '/case-method', '/pricing', '/consulting', '/results', '/mentors', '/deadlines-grid', '/diagnostic', '/portal', '/premium', '/compare', '/facts', '/schools', '/essay-guide', '/timeline-guide', '/calculator', '/resources', '/zip-code', '/elite-strategy', '/career-insights', '/career-compare', '/college-readiness', '/career-portal', '/why-hire', '/consultation', '/free-resources', '/blog'];

function PageTracker() {
  usePageTracker();
  return null;
}

function ScrollToTop() {
  const { pathname } = useLocation();
  React.useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function isMarketingPath(pathname) {
  if (MARKETING_PATHS.includes(pathname)) return true;
  // Individual school detail pages
  if (pathname.startsWith('/deadlines-grid/')) return true;
  return false;
}

function MarketingChatWidget() {
  const { pathname } = useLocation();
  if (!isMarketingPath(pathname)) return null;
  return <ChatWidget />;
}

function FloatingConsultationButton() {
  const { pathname } = useLocation();
  if (!isMarketingPath(pathname) || pathname === '/consultation') return null;
  return (
    <a href="/consultation"
      className="fixed bottom-20 right-4 md:bottom-6 md:right-6 z-40 flex items-center gap-2 px-5 py-3 bg-[#0F392B] text-white text-xs font-semibold rounded-full shadow-lg hover:bg-[#1a5c44] transition-all hover:scale-105"
      data-testid="floating-consultation-btn"
      style={{ boxShadow: '0 4px 20px rgba(15, 57, 43, 0.35)' }}>
      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
      Free Consultation
    </a>
  );
}

function StickyMobileCTA() {
  const { pathname } = useLocation();
  const [visible, setVisible] = React.useState(false);
  React.useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 250);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  if (!isMarketingPath(pathname)) return null;
  return (
    <div
      className={`md:hidden fixed bottom-0 inset-x-0 z-[45] transition-transform duration-300 ${visible ? 'translate-y-0' : 'translate-y-full'}`}
      data-testid="sticky-mobile-cta"
    >
      <div
        className="bg-[#0F392B] border-t border-white/10 px-4 py-2.5 flex items-center justify-between gap-3"
        style={{ paddingBottom: 'calc(0.625rem + env(safe-area-inset-bottom))', boxShadow: '0 -4px 20px rgba(15, 57, 43, 0.35)' }}
      >
        <div className="min-w-0">
          <p className="text-[11px] font-semibold text-white leading-tight truncate" style={{ fontFamily: "'Playfair Display', serif" }}>The Admissions Portal</p>
          <p className="text-[10px] text-white/60 leading-tight truncate">Every deadline, essay & rec — one command center</p>
        </div>
        <Link to="/sign-up" onClick={() => analytics.ctaPlatformStart('sticky-mobile')} className="flex-shrink-0" data-testid="sticky-mobile-cta-btn">
          <span className="inline-flex items-center gap-1.5 bg-[#D4AF37] text-[#0F392B] text-xs font-bold px-4 py-2.5 rounded-full shadow-lg">
            Start the Platform — $99 <ArrowRight className="h-3.5 w-3.5" />
          </span>
        </Link>
      </div>
    </div>
  );
}

function App() {
  return (
    <HelmetProvider>
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <PageTracker />
        <ScrollToTop />
        <MarketingChatWidget />
        <FloatingConsultationButton />
        <StickyMobileCTA />
        <Routes>
          {/* Public Marketing Pages */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/case-method" element={<CaseMethodPage />} />
          <Route path="/pricing" element={<ConsultingPage />} />
          <Route path="/consulting" element={<ConsultingPage />} />
          <Route path="/results" element={<ResultsPage />} />
          <Route path="/mentors" element={<MentorsPage />} />
          <Route path="/deadlines-grid" element={<DeadlinesGridPage />} />
          <Route path="/deadlines-grid/state/:code" element={<DeadlinesStatePage />} />
          <Route path="/deadlines-grid/no-application-fee" element={<DeadlinesCollectionPage collectionKey="no-application-fee" />} />
          <Route path="/deadlines-grid/test-optional" element={<DeadlinesCollectionPage collectionKey="test-optional" />} />
          <Route path="/deadlines-grid/rolling-admissions" element={<DeadlinesCollectionPage collectionKey="rolling-admissions" />} />
          <Route path="/deadlines-grid/early-decision-2" element={<DeadlinesCollectionPage collectionKey="early-decision-2" />} />
          <Route path="/deadlines-grid/early-action" element={<DeadlinesCollectionPage collectionKey="early-action" />} />
          <Route path="/deadlines-grid/:slug" element={<DeadlinesSchoolPage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />
          <Route path="/diagnostic" element={<DiagnosticPage />} />
          <Route path="/portal" element={<PortalPage />} />
          <Route path="/premium" element={<PremiumPage />} />
          <Route path="/compare" element={<ComparePage />} />
          <Route path="/facts" element={<AdmissionsFactsPage />} />
          <Route path="/schools" element={<SchoolProfilesPage />} />
          <Route path="/essay-guide" element={<EssayGuidePage />} />
          <Route path="/timeline-guide" element={<TimelineGuidePage />} />
          <Route path="/calculator" element={<CalculatorPage />} />
          <Route path="/resources" element={<ResourcesPage />} />
          <Route path="/zip-code" element={<ZipCodePage />} />
          <Route path="/elite-strategy" element={<EliteStrategyPage />} />
          <Route path="/career-insights" element={<CareerInsightsPage />} />
          <Route path="/career-compare" element={<CareerComparePage />} />
          <Route path="/college-readiness" element={<CollegeReadinessPage />} />
          <Route path="/career-portal" element={<CareerPortalPage />} />
          <Route path="/why-hire" element={<WhyHirePage />} />
          <Route path="/free-resources" element={<CantAffordPage />} />
          <Route path="/consultation" element={<ConsultationPage />} />

          {/* Auth */}
          <Route path="/sign-in" element={<PublicRoute><SignInPage /></PublicRoute>} />
          <Route path="/sign-up" element={<PublicRoute><SignUpPage /></PublicRoute>} />

          {/* Payment */}
          <Route path="/paywall" element={<ProtectedRoute bypassPaywall><PaywallPage /></ProtectedRoute>} />
          <Route path="/payment/success" element={<ProtectedRoute bypassPaywall><PaymentSuccessPage /></ProtectedRoute>} />

          {/* App */}
          <Route path="/dashboard" element={<ProtectedRoute><AppLayout><Dashboard /></AppLayout></ProtectedRoute>} />
          <Route path="/school-explorer" element={<ProtectedRoute><AppLayout><SchoolExplorerPage /></AppLayout></ProtectedRoute>} />
          <Route path="/colleges" element={<ProtectedRoute><AppLayout><CollegeList /></AppLayout></ProtectedRoute>} />
          <Route path="/essays" element={<ProtectedRoute><AppLayout><EssayStudio /></AppLayout></ProtectedRoute>} />
          <Route path="/activities" element={<ProtectedRoute><AppLayout><ActivitiesPage /></AppLayout></ProtectedRoute>} />
          <Route path="/documents" element={<ProtectedRoute><AppLayout><DocumentVault /></AppLayout></ProtectedRoute>} />
          <Route path="/recommenders" element={<ProtectedRoute><AppLayout><RecommendersPage /></AppLayout></ProtectedRoute>} />
          <Route path="/timeline" element={<ProtectedRoute><AppLayout><TimelinePage /></AppLayout></ProtectedRoute>} />
          <Route path="/profile" element={<ProtectedRoute><AppLayout><ProfilePage /></AppLayout></ProtectedRoute>} />
          <Route path="/resume" element={<ProtectedRoute><AppLayout><ResumePage /></AppLayout></ProtectedRoute>} />
          <Route path="/interview" element={<ProtectedRoute><AppLayout><InterviewPrepPage /></AppLayout></ProtectedRoute>} />
          <Route path="/checklist" element={<ProtectedRoute><AppLayout><ChecklistPage /></AppLayout></ProtectedRoute>} />
          <Route path="/admin" element={<ProtectedRoute bypassPaywall><AdminPage /></ProtectedRoute>} />

          {/* Law Portal */}
          <Route path="/law-portal" element={<ProtectedRoute><LawPortalLayout><LawDashboard /></LawPortalLayout></ProtectedRoute>} />
          <Route path="/law-portal/profile" element={<ProtectedRoute><LawPortalLayout><LawProfile /></LawPortalLayout></ProtectedRoute>} />
          <Route path="/law-portal/schools" element={<ProtectedRoute><LawPortalLayout><LawSchools /></LawPortalLayout></ProtectedRoute>} />
          <Route path="/law-portal/applications" element={<ProtectedRoute><LawPortalLayout><LawApplications /></LawPortalLayout></ProtectedRoute>} />
          <Route path="/law-portal/essays" element={<ProtectedRoute><LawPortalLayout><LawEssays /></LawPortalLayout></ProtectedRoute>} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
